import Breadcrumb from 'components/Navigation/Breadcrumb';
import React, { useEffect, useState } from 'react';
import male from '../../../../assets/male-big.png';
import female from '../../../../assets/female-big.png';
import Button from 'components/Form/Button';
import StatCard from 'components/Cards/UserDashboard/StatCard';
import { Link, useNavigate, useParams } from 'react-router-dom';
import withCreatePortal from 'components/HOC/withCreatePortal';
import DeactivateModal from 'apps/admin/pages/Customer/DeactivateModal';
import {
  useActivateCustomer,
  useGetArchivedCustomer,
  useGetCustomerDetails,
  useGetCustomerStats
} from 'common/queries-and-mutations/admin/customer';
import { Loader } from 'components/Loader';
import { Gender } from 'apps/user/types/user.enum.type';
import { format } from 'date-fns';
import ConfirmModal from 'components/Modal/ConfirmModal';
import ArchiveModal from './ArchiveModal';
import SuccessModal from 'components/Modal/SuccessModal';
import CustomerActivitySummary from './CustomerActivitySummary';

const EnhancedDeactivateModal = withCreatePortal(DeactivateModal);
const EnhanceArchiveModal = withCreatePortal(ArchiveModal);
const EnhancedConfirmModal = withCreatePortal(ConfirmModal);
const EnhancedSuccessModal = withCreatePortal(SuccessModal);

export default function CustomerDetails() {
  const navigate = useNavigate();
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { customerId } = useParams();
  const [showAskToConfirm, setShowAskToConfirm] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [action, setAction] = useState('');

  const { data, isLoading, isFetched, refetch } = useGetCustomerDetails({
    customerId: +customerId!
  });

  const { data: archive, refetch: getArchived } = useGetArchivedCustomer({
    customerId: +customerId!,
    enabled: !data?.isArchived
  });

  const { mutate, isSuccess, isError } = useActivateCustomer();

  const { data: stats, isLoading: isStatLoading } = useGetCustomerStats({
    customerId: +customerId!
  });

  const handleCustomerActivate = () => {
    mutate({ customerId: +customerId! });
  };

  useEffect(() => {
    if (isSuccess && !isError) {
      setIsSubmitting(false);
      setShowSuccess(true);
      refetch();
    }
    if (isError) {
      setIsSubmitting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess, isError]);

  if (isLoading && isFetched) {
    return (
      <div className="mt-40 flex flex-col items-center justify-center">
        <Loader color={'#FF6606'} height={50} width={50} />
      </div>
    );
  }

  return (
    <div>
      <Breadcrumb title="Customer Profile" />
      <div className="mt-5 pb-20">
        <div className="mb-5 flex flex-row rounded bg-white">
          <div className="flex w-1/5 flex-col items-center justify-center border-r px-6">
            <div className="flex flex-col items-center justify-center gap-3">
              <img src={data?.gender === Gender.MALE ? male : female} />
              <p className="text-xl font-bold text-[#353535]">{`${data?.othername} ${data?.surname}`}</p>
            </div>
          </div>
          <div className="w-full">
            <div className="border-b border-[#E5E5E5]">
              <div className="mt-3 grid grid-cols-4 gap-x-16 px-6">
                <div className="col-span-1 px-8 py-6">
                  <p className="text-[10px] font-normal text-[#7A7C87]">Name</p>
                  <p className="mt-1 text-sm font-normal text-[#353535]">
                    {`${data?.othername} ${data?.surname}`}
                  </p>
                </div>
                <div className="col-span-1 px-8 py-6">
                  <p className="text-[10px] font-normal text-[#7A7C87]">
                    Date of Birth
                  </p>
                  <p className="mt-1 text-sm font-normal text-[#353535]">
                    {data?.dateOfBirth
                      ? format(new Date(data.dateOfBirth), 'dd MMMM yyyy')
                      : 'Unknown'}
                  </p>
                </div>
                <div className="col-span-1 px-8 py-6">
                  <p className="text-[10px] font-normal text-[#7A7C87]">
                    Gender
                  </p>
                  <p className="mt-1 text-sm font-normal text-[#353535]">
                    {data?.gender}
                  </p>
                </div>
                <div className="col-span-1 px-8 py-6">
                  <p className="text-[10px] font-normal text-[#7A7C87]">
                    Marital Status
                  </p>
                  <p className="mt-1 text-sm font-normal text-[#353535]">
                    {data?.maritalStatus}
                  </p>
                </div>
              </div>
            </div>
            <div className="border-b border-[#E5E5E5]">
              <div className="grid grid-cols-4 gap-x-16 px-6">
                <div className="col-span-1 px-8 py-4">
                  <p className="text-[10px] font-normal text-[#7A7C87]">
                    Phone Number
                  </p>
                  <p className="mt-1 text-sm font-normal text-[#353535]">
                    {data?.phoneNumber}
                  </p>
                </div>
                <div className="col-span-1 px-8 py-4">
                  <p className="text-[10px] font-normal text-[#7A7C87]">
                    Email address
                  </p>
                  <p className="mt-1 text-sm font-normal text-[#353535]">
                    {data?.email}
                  </p>
                </div>
                <div className="col-span-1 px-8 py-4">
                  <p className="text-[10px] font-normal text-[#7A7C87]">
                    Home Address
                  </p>
                  <p className="mt-1 text-sm font-normal text-[#353535]">
                    {data?.homeAddress}
                  </p>
                </div>
              </div>
            </div>
            <div className="p-5">
              <div className="flex items-center justify-between gap-1">
                <div>
                  <Button
                    variant="secondary"
                    size="custom"
                    className={`h-5 w-[10rem] rounded-[3px] border-[#FF6600] bg-[#FF66061A] p-4 text-sm font-medium text-[#FF6606] ${
                      data?.isArchived ? 'cursor-not-allowed' : ''
                    }`}
                    label={`${
                      data?.isArchived ? 'Archived' : 'Archive Customer'
                    }`}
                    type="button"
                    disabled={data?.isArchived || isSubmitting}
                    loading={isSubmitting}
                    onClick={() => {
                      setShowArchiveModal(true);
                      setAction('Archived');
                    }}
                  ></Button>
                  {data?.isArchived && archive ? (
                    <div className="flex flex-col">
                      <div className="mt-4 flex items-center gap-2">
                        <p className="text-sm font-normal">Reason: </p>
                        <p className="text-sm font-normal text-red-600">
                          {archive?.reasonForArchived}
                        </p>
                      </div>
                      <div className="mt-3 flex items-center gap-2">
                        <p className="text-sm font-normal">Archive Date: </p>
                        <p className="text-sm font-normal text-red-600">
                          {archive?.archivedDate
                            ? format(
                                new Date(archive.archivedDate),
                                'dd MMMM yyyy'
                              )
                            : 'Unknown'}
                        </p>
                      </div>
                    </div>
                  ) : (
                    <></>
                  )}
                </div>

                {!data?.isArchived && (
                  <>
                    {data?.isActive ? (
                      <Button
                        variant="primary"
                        size="custom"
                        className="h-5 w-[10rem] rounded-[3px] p-2.5 text-sm font-medium"
                        label="Deactivate Customer"
                        type="button"
                        disabled={isSubmitting}
                        loading={isSubmitting}
                        onClick={() => {
                          setShowDeactivateModal(true);
                          setAction('Deactivated');
                        }}
                      ></Button>
                    ) : (
                      <Button
                        variant="primary"
                        size="custom"
                        className="h-5 w-[10rem] rounded-[3px] bg-[#00B388] p-2.5 text-sm font-medium hover:bg-[#00B388]"
                        label="Activate Customer"
                        type="button"
                        onClick={() => {
                          setShowAskToConfirm(true);
                          setAction('Activated');
                        }}
                        disabled={isSubmitting}
                        loading={isSubmitting}
                      ></Button>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="my-3 grid grid-cols-1 gap-4 tablet:grid-cols-3">
          <Button
            variant="secondary"
            size="custom"
            className="h-12 w-[23.5rem] rounded border-none bg-[#FF4E75] p-2.5 font-medium text-white hover:bg-[#FF4E75]"
            label="View Status"
            type="button"
            onClick={() => navigate(`/admin/customers/will-info/${data?.id}`)}
          ></Button>
          <Button
            variant="secondary"
            size="custom"
            className="h-12 w-[23.5rem] rounded border-none bg-[#00B388] p-2.5 font-medium text-white hover:bg-[#00B388]"
            label="Payment History"
            type="button"
            onClick={() => navigate(`/admin/customers/payments/${data?.id}`)}
          ></Button>
          <Button
            variant="secondary"
            size="custom"
            className="h-12 w-[23.5rem] rounded border-primary-800 bg-primary-800 p-2.5 font-medium text-white"
            label="Assign To Staff"
            type="button"
            onClick={() =>
              navigate(`/admin/customers/assign-staff/${customerId}`)
            }
          ></Button>
        </div>

        <div className="my-6 flex items-center justify-between">
          <p className="text-2xl font-bold text-[#353535]">Asset Allotment</p>
          <div className="flex items-center gap-5">
            <Link to={`/admin/customers/will-history/${customerId}`}>
              <Button
                variant="secondary"
                size="custom"
                className="h-8 w-[8rem] rounded border-none bg-[#585858] p-1.5 px-2 text-sm font-medium text-white"
                label="Will History"
                type="button"
              ></Button>
            </Link>
            <Link to={`/admin/customers/will-summary/${customerId}`}>
              <Button
                variant="secondary"
                size="custom"
                className="h-8 rounded border-none bg-[#585858] p-1.5 px-2 text-sm font-medium text-white"
                label="Check Will Summary"
                type="button"
              ></Button>
            </Link>
          </div>
        </div>

        <div className="my-5 grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4">
          <div className="col-span-12 flex space-x-5 overflow-x-auto px-[var(--margin-x)] transition-all duration-[.25s] scrollbar-hide">
            {isStatLoading && !stats ? (
              <div className="mt-10 flex flex-col items-center justify-center">
                <Loader color={'#FF6606'} height={50} width={50} />
              </div>
            ) : (
              (stats ?? [])?.map(stat => (
                <StatCard
                  progress={stat.totalAssigned ?? 0 / stat.totalAccount ?? 0}
                  key={stat.pageId}
                  icon={stat.icon}
                  name={stat.name}
                  totalAccount={stat.totalAccount}
                  totalBeneficiary={stat.totalBeneficiary}
                  pageUrl={stat.pageUrl}
                  showAssign={false}
                />
              ))
            )}
          </div>
        </div>
        <CustomerActivitySummary customerId={+customerId!} />
      </div>

      {showDeactivateModal && (
        <EnhancedDeactivateModal
          onSuccess={() => {
            refetch();
            setShowDeactivateModal(false);
            setShowSuccess(true);
          }}
          onClose={() => setShowDeactivateModal(false)}
          className="w-[30%]"
          customerId={+customerId!}
        />
      )}
      {showAskToConfirm && (
        <EnhancedConfirmModal
          content={`Are you sure you want to activate this customer?`}
          onConfirm={() => {
            setShowAskToConfirm(false);
            handleCustomerActivate();
          }}
          onCancel={() => {
            setShowAskToConfirm(false);
          }}
          actionText="Yes"
          cancelText="No"
          title="Action Required"
        />
      )}

      {showSuccess && (
        <EnhancedSuccessModal
          title={`Customer Successfully ${action}`}
          onProccessClick={() => {
            setShowSuccess(false);
          }}
          buttonText="Back to Customer"
        />
      )}
      {showArchiveModal && (
        <EnhanceArchiveModal
          onSuccess={() => {
            refetch();
            getArchived();
            setShowArchiveModal(false);
            setShowSuccess(true);
          }}
          onClose={() => setShowArchiveModal(false)}
          className="w-[30%]"
          customerId={+customerId!}
        />
      )}
    </div>
  );
}
